import clsx from "clsx";
import {
  FunctionComponent,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from "react";

import type { GridCellPlacement } from "@story/data-centers-hub/config/slides";

import * as styles from "./GridSlide.css";

interface Props {
  cells: ReadonlyArray<GridCellPlacement>;
  /** Optional per-cell content keyed by cell id. Falls back to a labeled placeholder. */
  renderCell?: (cell: GridCellPlacement) => ReactNode;
  /** Overlay drawn above the grid (e.g. title words that span cells). */
  overlay?: ReactNode;
  /** Topmost layer (e.g. intro covers) — above overlay. */
  forelayer?: ReactNode;
  className?: string;
  style?: CSSProperties;
  rootRef?: Ref<HTMLDivElement>;
  "aria-label"?: string;
  "aria-hidden"?: boolean | "true" | "false";
}

function DefaultCellContent({ cell }: { cell: GridCellPlacement }) {
  if (cell.split === "horizontal") {
    return (
      <div className={styles.splitStack}>
        <div className={styles.splitPane}>
          <div className={styles.placeholder}>{cell.label ?? cell.id}</div>
        </div>
        <div className={styles.splitPane}>
          <div className={styles.placeholder}>{cell.label ?? cell.id}</div>
        </div>
      </div>
    );
  }

  return <div className={styles.placeholder}>{cell.label ?? cell.id}</div>;
}

/**
 * Desktop 3×2 grid slide. Merged cells use `column` / `row` spans from config.
 * Each cell is `overflow: hidden` so intro / cross-slide swipes can clip.
 */
const GridSlide: FunctionComponent<Props> = ({
  cells,
  renderCell,
  overlay,
  forelayer,
  className,
  style,
  rootRef,
  "aria-label": ariaLabel,
  "aria-hidden": ariaHidden,
}) => {
  return (
    <div
      ref={rootRef}
      className={clsx(styles.grid, className)}
      style={style}
      role="group"
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    >
      {cells.map((cell) => {
        const placement: CSSProperties = {
          gridColumn: cell.column,
          gridRow: cell.row,
        };
        return (
          <div
            key={cell.id}
            className={styles.cell}
            style={placement}
            data-cell={cell.id}
          >
            <div className={styles.cellInner}>
              {renderCell ? (
                renderCell(cell)
              ) : (
                <DefaultCellContent cell={cell} />
              )}
            </div>
          </div>
        );
      })}
      {overlay}
      {forelayer}
    </div>
  );
};

export default GridSlide;
